import React, { useState, useCallback, useRef } from 'react'
import Cropper from 'react-easy-crop'
import {
  Button,
  Box,
  Slider,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material'
import { Upload, CameraAlt, VideocamOff } from '@mui/icons-material'
import { getCroppedImg } from './cropUtils' // Implement this utility to crop the image.
import { useAtom } from 'jotai'
import { customImagesFamilyAtom } from 'atoms'
import Webcam from 'react-webcam' // Import react-webcam
import { useTranslation } from 'react-i18next'

const ImageSelector = ({ imageKey }) => {
  const [croppedImage, setCroppedImage] = useAtom(
    customImagesFamilyAtom(imageKey)
  )
  const { t } = useTranslation()
  const [selectedImage, setSelectedImage] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [isWebcamOpen, setIsWebcamOpen] = useState(false) // Toggle webcam
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false) // Toggle crop dialog
  const webcamRef = useRef(null) // Move this to the top level

  // Handle file upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = () => {
        setSelectedImage(reader.result) // Set the uploaded image
        setIsCropDialogOpen(true) // Open the crop dialog after uploading
      }
      reader.readAsDataURL(file)
    }
  }

  // Capture image from webcam
  const captureFromWebcam = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot()
      setSelectedImage(imageSrc)
      setIsWebcamOpen(false) // Close the webcam modal
      setIsCropDialogOpen(true) // Open crop dialog
    }
  }

  // When crop is complete, save the cropped area
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  // Generate the cropped image
  const makeCroppedImage = useCallback(async () => {
    if (selectedImage && croppedAreaPixels) {
      try {
        const croppedImage = await getCroppedImg(
          selectedImage,
          croppedAreaPixels
        )
        console.log('donee', { croppedImage })
        setCroppedImage(croppedImage) // Save cropped image to Jotai atom
        setIsCropDialogOpen(false) // Close crop dialog
      } catch (error) {
        console.error('Error cropping image:', error)
      }
    }
  }, [selectedImage, croppedAreaPixels, setCroppedImage])

  // Remove the image and reset states
  const removeImage = () => {
    setSelectedImage(null)
    setCroppedImage(null) // Clear the image from Jotai atom
  }

  if (croppedImage) {
    return (
      <div>
        <Button
          variant="outlined"
          color="secondary"
          onClick={removeImage}
          component="span"
          style={{
            marginBottom: '20px',
            marginRight: '20px',
            minWidth: '180px',
          }}
        >
          {t('removeImage')}
        </Button>
      </div>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Material-UI Styled Upload Button */}
      <input
        accept="image/*"
        type="file"
        onChange={handleImageUpload}
        id="file-upload"
        style={{ display: 'none' }}
      />
      <label htmlFor="file-upload">
        <Button
          variant="contained"
          color="secondary"
          component="span"
          style={{
            marginBottom: '20px',
            marginRight: '20px',
            minWidth: '180px',
          }}
          startIcon={<Upload />} // Add an upload icon
        >
          {t('uploadImage')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setIsWebcamOpen(!isWebcamOpen)}
          style={{ marginBottom: '20px', minWidth: '180px' }}
          startIcon={isWebcamOpen ? <VideocamOff /> : <CameraAlt />} // Dynamic icon
        >
          {isWebcamOpen ? t('closeWebcam') : t('openWebcam')}
        </Button>
      </label>

      {/* Open Webcam in Modal */}
      <Dialog
        open={isWebcamOpen}
        onClose={() => setIsWebcamOpen(false)}
        maxWidth="lg" // Large width for webcam modal
      >
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', height: 400 }}>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsWebcamOpen(false)} color="primary">
            {t('close')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={captureFromWebcam}
          >
            {t('captureImage')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Cropper Dialog */}
      <Dialog
        open={isCropDialogOpen}
        onClose={() => setIsCropDialogOpen(false)}
        maxWidth="lg" // Large width for crop modal
        fullWidth
      >
        <DialogContent>
          {selectedImage && (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 400,
                backgroundColor: '#333',
              }}
            >
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1} // 1 means a square crop, change to 16/9 for wide screen aspect ratio
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>
          )}

          {/* Zoom Slider */}
          <Box sx={{ width: '100%', marginTop: 2 }}>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, newValue) => setZoom(newValue)}
              valueLabelDisplay="auto"
              aria-labelledby="zoom-slider"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCropDialogOpen(false)} color="primary">
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={makeCroppedImage}
          >
            {t('saveCrop')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ImageSelector
